import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { compact } from 'lodash-es';
import { TableList, Typography, DropdownMenu, TableListActions, WithInfoIcon, Tooltip, useOverlayContext, useDevice } from 'seagull';
import { ActivityLog, AgreementEdit, Coins, Copy, Document, Download, EditSquare, Eye, InvoiceEdit, LinkIcon, Trash, User, WalletCheck } from 'seagull/icons';
import { ActivityLogBottomSheetContent } from '~/components/ActivityLogBottomSheetContent/ActivityLogBottomSheetContent';
import EnterAccountantAccessButton from '~/components/EnterAccountantAccessButton/EnterAccountantAccessButton';
import { routes } from '~/constants';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { relationshipModel, relationshipSummaryModel, timeModel } from '~/models';
import styles from './AgreementsDashboardRow.module.scss';
import ShareAgreementLinksModal from './ShareAgreementLinksModal';
const agreementStatusToText = {
    [relationshipSummaryModel.AgreementStatus.Active]: 'Active',
    [relationshipSummaryModel.AgreementStatus.Exhausted]: 'Completed',
    [relationshipSummaryModel.AgreementStatus.Terminated]: 'Terminated'
};
export default function AgreementsDashboardRow(props) {
    const { isVendor, index, hasAccountantAccess, onVerifyPaymentMethod, onEditAgreement, onDownloadPDF, onDuplicateAgreement, onTerminateAgreement, onEnterAccountantAccess, onRenameAgreement, onChangeAssignee } = props;
    const { t: dashboardTexts } = useLocaleKeys('pages.agreementsDashboard');
    const { t: rowTooltipTexts } = useLocaleKeys('pages.agreementsDashboard.rowTooltips');
    const { t: rowActionTexts } = useLocaleKeys('pages.agreementsDashboard.rowActions');
    const [agreement, setAgreement] = useState(props.agreement);
    useEffect(()=>setAgreement(props.agreement), [
        props.agreement
    ]);
    const overlayContext = useOverlayContext();
    const { isMobileDevice } = useDevice();
    const history = useHistory();
    const displayedTitle = relationshipSummaryModel.displayedTitle(agreement, isVendor);
    const testId = agreement.isSample ? 'test-agreement' : "agreements-row-".concat(index);
    const heapId = agreement.isSample ? 'test-agreement' : 'agreement';
    const isTerminated = relationshipSummaryModel.isTerminated(agreement);
    const canCompleteVerification = relationshipSummaryModel.canCompleteVerification(agreement);
    const agreementStatus = relationshipSummaryModel.agreementStatus(agreement);
    const canRequestPM = (!agreement.fundingSource || agreement.fundingSource.failedVerification()) && !relationshipSummaryModel.isTerminated(agreement) && isVendor;
    const agreementLink = isVendor || !relationshipModel.isPending(agreement) ? routes.agreement(agreement.id) : routes.clientOnboarding(agreement.id);
    const items = compact([
        {
            value: /*#__PURE__*/ _jsxs("div", {
                className: styles.title,
                children: [
                    /*#__PURE__*/ _jsxs(Typography, {
                        ellipsis: true,
                        hideTooltip: true,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                ellipsis: true,
                                weight: "normal",
                                testId: "".concat(testId, "-company-name"),
                                tooltipTestId: "".concat(testId, "-company-name-tooltip"),
                                children: relationshipSummaryModel.opposingPartyName(agreement, isVendor)
                            }),
                            displayedTitle && /*#__PURE__*/ _jsx(Typography, {
                                ellipsis: true,
                                variant: "paragraph2",
                                skin: "secondary",
                                testId: "".concat(testId, "-title"),
                                tooltipTestId: "".concat(testId, "-title-tooltip"),
                                children: displayedTitle
                            })
                        ]
                    }),
                    hasAccountantAccess && /*#__PURE__*/ _jsx(EnterAccountantAccessButton, {
                        testId: "".concat(testId, "-enter-acc-access"),
                        onClick: (e)=>{
                            e.stopPropagation(); // Prevents the row <button> element from redirecting to its target.
                            onEnterAccountantAccess(relationshipSummaryModel.customerBusinessId(agreement), relationshipSummaryModel.customerBusinessName(agreement));
                        }
                    })
                ]
            })
        },
        {
            value: /*#__PURE__*/ _jsxs("div", {
                className: styles.status,
                children: [
                    /*#__PURE__*/ _jsx(WithInfoIcon, {
                        showInfo: agreementStatus === relationshipSummaryModel.AgreementStatus.Exhausted,
                        info: rowActionTexts.allServicesCompleted(),
                        infoTestId: "".concat(testId, "-status-info"),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            testId: "".concat(testId, "-status"),
                            children: agreementStatusToText[agreementStatus]
                        })
                    }),
                    agreement.hasAmendments && /*#__PURE__*/ _jsx(Tooltip, {
                        placement: "top",
                        title: rowTooltipTexts.awaitingApproval(),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            testId: "".concat(testId, "-has-amendments"),
                            children: /*#__PURE__*/ _jsx(InvoiceEdit, {
                                size: 16
                            })
                        })
                    })
                ]
            })
        },
        isVendor && {
            value: /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                skin: "secondary",
                testId: "".concat(testId, "-assignee"),
                children: relationshipSummaryModel.sellerFullName(agreement)
            })
        },
        {
            value: /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                children: agreement.effectiveDate ? timeModel.formatDateLong(agreement.effectiveDate) : ''
            })
        },
        {
            value: !agreement.fundingSource ? /*#__PURE__*/ _jsx(Typography, {
                skin: "error",
                children: dashboardTexts.notSet()
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(Typography, {
                        skin: "secondary",
                        children: [
                            agreement.fundingSource.isCreditCard() ? 'Card' : 'Bank account',
                            " (",
                            agreement.fundingSource.last4(),
                            ")"
                        ]
                    }),
                    agreement.fundingSource.failedVerification() && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "error",
                        children: dashboardTexts.verificationFailed()
                    }),
                    agreement.fundingSource.isPendingMicroDeposit() && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "warning",
                        children: agreement.fundingSource.isPendingAutomaticVerification() ? 'Automatic verification initiated' : 'Manual verification initiated'
                    })
                ]
            })
        },
        {
            value: agreement.lastBilledDate ? /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                testId: "".concat(testId, "-last-billed"),
                children: timeModel.formatDateLong(agreement.lastBilledDate)
            }) : null
        }
    ]);
    const getButtonText = ()=>{
        if (canCompleteVerification) return rowActionTexts.completeVerification();
        return canRequestPM ? rowActionTexts.addPaymentMethod() : undefined;
    };
    const onButtonClick = ()=>{
        if (canCompleteVerification) {
            onVerifyPaymentMethod(agreement);
        } else {
            history.push(routes.paymentMethods(agreement.id));
        }
    };
    return /*#__PURE__*/ _jsx(TableList.Row, {
        onClick: ()=>history.push(agreementLink),
        items: items,
        rowId: agreement.id,
        testId: testId,
        "data-heapid": heapId,
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            testId: testId,
            menuHeapId: "".concat(heapId, "-menu-btn"),
            buttonText: getButtonText(),
            buttonIcon: /*#__PURE__*/ _jsx(Coins, {}),
            secondaryButtonText: rowActionTexts.view(),
            secondaryBtnHeapId: "".concat(heapId, "-view-btn"),
            secondaryButtonIcon: /*#__PURE__*/ _jsx(Eye, {}),
            onClickButton: onButtonClick,
            onClickSecondaryButton: ()=>history.push(agreementLink),
            mobile: {
                title: relationshipSummaryModel.opposingPartyName(agreement, isVendor),
                subtitle: displayedTitle !== null && displayedTitle !== void 0 ? displayedTitle : undefined
            },
            children: [
                isVendor && !isTerminated && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(AgreementEdit, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-edit-agreement"),
                    onClick: ()=>onEditAgreement(agreement),
                    bi: {
                        name: 'edit_agreement'
                    },
                    children: rowActionTexts.editAgreement()
                }),
                isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(EditSquare, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-rename-agreement"),
                    onClick: ()=>onRenameAgreement(agreement, (title)=>{
                            const newAgreement = relationshipSummaryModel.setTitle(agreement, title);
                            setAgreement(newAgreement);
                        }),
                    bi: {
                        name: 'rename_agreement'
                    },
                    children: rowActionTexts.renameAgreement()
                }),
                isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(User, {}),
                    testId: "".concat(testId, "-change-assignee"),
                    onClick: ()=>onChangeAssignee(agreement),
                    bi: {
                        name: 'change_assignee'
                    },
                    children: rowActionTexts.changeAssignee()
                }),
                isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    intent: Intent.DuplicateAgreement,
                    iconStart: /*#__PURE__*/ _jsx(Copy, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-duplicate"),
                    "data-heapid": "".concat(heapId, "-menu-duplicate"),
                    onClick: ()=>onDuplicateAgreement(agreement),
                    bi: {
                        name: 'duplicate_agreement'
                    },
                    children: rowActionTexts.duplicateAgreement()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    intent: Intent.DownloadAgreementPdf,
                    iconStart: /*#__PURE__*/ _jsx(Download, {}),
                    testId: "".concat(testId, "-download-pdf"),
                    "data-heapid": "".concat(heapId, "-menu-download-pdf"),
                    onClick: ()=>onDownloadPDF(agreement),
                    bi: {
                        name: 'download_pdf'
                    },
                    children: rowActionTexts.downloadPdf()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(LinkIcon, {}),
                    testId: "".concat(testId, "-direct-links"),
                    onClick: ()=>{
                        overlayContext.showModal({
                            closeOnClickOutside: true,
                            closeOnEscapeKey: true,
                            openBottomSheetOnMobile: true,
                            hideBottomSheetOnDismiss: true,
                            content: (p)=>/*#__PURE__*/ _jsx(ShareAgreementLinksModal, _object_spread_props(_object_spread({}, p), {
                                    relationship: agreement
                                }))
                        });
                    },
                    bi: {
                        name: 'share_direct_link'
                    },
                    children: rowActionTexts.shareDirectLink()
                }),
                !isTerminated && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    as: Link,
                    to: routes.billingHub(agreement.id),
                    iconStart: /*#__PURE__*/ _jsx(WalletCheck, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-billing-hub"),
                    "data-heapid": "".concat(heapId, "-menu-billing-hub"),
                    bi: {
                        name: 'manage_billing'
                    },
                    children: rowActionTexts.manageBilling()
                }),
                !isTerminated && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    as: Link,
                    to: routes.invoices(agreement.id),
                    iconStart: /*#__PURE__*/ _jsx(Document, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-invoices"),
                    "data-heapid": "".concat(heapId, "-menu-invoices"),
                    bi: {
                        name: 'view_invoices'
                    },
                    children: rowActionTexts.viewInvoices()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, _object_spread_props(_object_spread({
                    iconStart: /*#__PURE__*/ _jsx(ActivityLog, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-activity"),
                    "data-heapid": "".concat(heapId, "-menu-activity"),
                    bi: {
                        name: 'view_activity_log'
                    }
                }, isMobileDevice ? {
                    onClick: ()=>{
                        overlayContext.pushBottomSheetPage({
                            content: /*#__PURE__*/ _jsx(ActivityLogBottomSheetContent, {
                                agreementId: agreement.id
                            })
                        });
                    }
                } : {
                    as: Link,
                    to: routes.activityLog(agreement.id)
                }), {
                    children: rowActionTexts.viewActivityLog()
                })),
                isVendor && !isTerminated && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    intent: Intent.TerminateAgreement,
                    skin: "error",
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    testId: "".concat(testId, "-terminate"),
                    "data-heapid": "".concat(heapId, "-menu-terminate"),
                    onClick: ()=>onTerminateAgreement(agreement),
                    bi: {
                        name: 'terminate_agreement'
                    },
                    children: rowActionTexts.terminateAgreement()
                })
            ]
        })
    });
}
